import type { ApiError } from "@cx/contracts";
import { ApiFailure } from "./api";

/**
 * Baixa um texto gerado no navegador (CSV das exportações). O nome é sanitizado: só letras, números, "-", "_" e ".".
 */
export function downloadText(filename: string, content: string, type = "text/csv;charset=utf-8"): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.replace(/[^\w.-]+/g, "-");
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Nome do arquivo do cabeçalho Content-Disposition (`filename="..."`), sanitizado; sem nome = o padrão. */
export function fileNameFromDisposition(header: string | null, fallback: string): string {
  const m = /filename="([^"]+)"/.exec(header ?? "");
  return (m?.[1] ?? fallback).replace(/[^\w.-]+/g, "-");
}

/**
 * Baixa um arquivo que a API devolve como anexo (remessa/retorno CNAB). Erro da API vira `ApiFailure`, como no
 * `apiFetch`. POST com corpo JSON quando `body` vem.
 */
export async function downloadApiFile(
  path: string,
  companyId: string,
  fallbackName: string,
  body?: unknown,
): Promise<string> {
  const headers = new Headers({ "x-company-id": companyId });
  if (body !== undefined) headers.set("content-type", "application/json");
  const res = await fetch(`/api/v1${path}`, {
    method: body === undefined ? "GET" : "POST",
    headers,
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const e = ((await res.json().catch(() => null)) as ApiError | null)?.error;
    throw new ApiFailure(
      res.status,
      e?.code ?? "http_error",
      e?.message ?? "Não foi possível baixar o arquivo.",
    );
  }
  const name = fileNameFromDisposition(res.headers.get("content-disposition"), fallbackName);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return name;
}

/**
 * POST que devolve um arquivo (200, baixado na hora) ou um job (202, `{ jobId }`) — impressão de etiquetas (T3.6).
 * `inline` devolve a URL do blob em vez de baixar (prévia em iframe; quem chama revoga).
 */
export async function postApiFile(
  path: string,
  companyId: string,
  fallbackName: string,
  body: unknown,
  o: { inline?: boolean } = {},
): Promise<{ jobId: string } | { name: string; url?: string }> {
  const res = await fetch(`/api/v1${path}`, {
    method: "POST",
    headers: { "x-company-id": companyId, "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const e = ((await res.json().catch(() => null)) as ApiError | null)?.error;
    throw new ApiFailure(
      res.status,
      e?.code ?? "http_error",
      e?.message ?? "Não foi possível gerar o arquivo.",
    );
  }
  if (res.status === 202) return (await res.json()) as { jobId: string };
  const name = fileNameFromDisposition(res.headers.get("content-disposition"), fallbackName);
  const url = URL.createObjectURL(await res.blob());
  if (o.inline) return { name, url };
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return { name };
}
